// Betting page — top-level orchestrator for The Club's side-games flow.
//
// Heavy components are split into:
//   • betting/constants.js      — GAME_TYPES, SIDE_BET_TYPES, GAME_RULES, API base
//   • betting/Scoring.js        — All per-game scoring views + shared scoring helpers + LiveScoring router + RoundSummary
//   • betting/Leaderboard.js    — Leaderboard view
//
// This file only owns the setup/list/route flow.

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Plus, Trophy, DollarSign, Users, ChevronRight, Clock, ArrowLeft, X, Search, MapPin, Loader2, HelpCircle, Mail as MailIcon, Check, Edit3, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';

import { useAuth } from '../contexts/AuthContext';
import { API, GAME_TYPES } from './betting/constants';
import { LiveScoring, GameRulesModal } from './betting/Scoring';
import { Leaderboard } from './betting/Leaderboard';

// =================== ROUND LIST ===================
const RoundList = ({ onCreateNew, onSelectRound }) => {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/betting/rounds`).then(r => setRounds(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const gameLabel = (g) => GAME_TYPES.find(t => t.value === g.type)?.label || g.type;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-serif text-lg font-bold text-foreground">My Games</h2>
          <p className="text-xs text-muted-foreground">{rounds.length} games played</p>
        </div>
        <Button onClick={onCreateNew} className="rounded-full gap-1.5 text-sm" data-testid="create-game-btn">
          <Plus className="w-4 h-4" /> New Game
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 rounded-xl animate-pulse bg-muted" />)}</div>
      ) : rounds.length === 0 ? (
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 mx-auto mb-3 text-muted" />
          <h3 className="font-serif text-lg font-semibold mb-2 text-foreground">No Games Yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Start a side game with your group</p>
          <Button onClick={onCreateNew} className="rounded-full gap-2" data-testid="create-first-game-btn">
            <Plus className="w-4 h-4" /> Start a Game
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {rounds.map(r => (
            <div key={r.id} onClick={() => onSelectRound(r.id)} className="p-4 rounded-xl border border-border bg-card cursor-pointer transition-all hover:border-primary/30" data-testid={`round-card-${r.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black bg-primary/10 text-primary">
                      {GAME_TYPES.find(t => t.value === r.games?.[0]?.type)?.icon || '?'}
                    </span>
                    <div>
                      <h3 className="font-semibold text-sm text-foreground">{gameLabel(r.games?.[0] || {})}</h3>
                      <p className="text-xs text-muted-foreground">{r.course_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {r.players.map(p => p.name).join(', ')}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {r.date}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${r.status === 'active' ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                    {r.status}
                  </span>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =================== CREATE: STEP 1 — SELECT GAME ===================
const GameSelector = ({ onSelect, onBack }) => {
  const [rulesGame, setRulesGame] = useState(null);
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full"><ArrowLeft className="w-5 h-5" /></Button>
        <h2 className="font-serif text-lg font-bold text-foreground">Choose Your Game</h2>
      </div>
      <div className="space-y-3">
        {GAME_TYPES.map(g => (
          <div
            key={g.value}
            className="p-4 rounded-xl border border-border bg-card cursor-pointer transition-all hover:border-primary/30 flex items-center gap-4"
            data-testid={`select-game-${g.value}`}
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-black bg-primary/10 text-primary" onClick={() => onSelect(g.value)}>{g.icon}</div>
            <div className="flex-1" onClick={() => onSelect(g.value)}>
              <h3 className="font-semibold text-foreground">{g.label}</h3>
              <p className="text-xs text-muted-foreground">{g.desc}</p>
              <p className="text-[10px] font-bold text-primary mt-0.5">{g.players} players</p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setRulesGame(g.value); }}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted transition-colors flex-shrink-0"
              data-testid={`rules-btn-${g.value}`}
            >
              <HelpCircle className="w-4.5 h-4.5 text-muted-foreground" />
            </button>
          </div>
        ))}
      </div>
      <GameRulesModal gameType={rulesGame} open={!!rulesGame} onClose={() => setRulesGame(null)} />
    </div>
  );
};

// =================== COURSE SEARCH (reusable) ===================
const CourseSearchInput = ({ courseName, onCourseSelect, onCourseNameChange }) => {
  const { user } = useAuth();
  const [query, setQuery] = useState(courseName || '');
  const [courses, setCourses] = useState([]);
  const [nearbyCourses, setNearbyCourses] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchNearby = async () => {
      setLoadingNearby(true);
      try {
        let lat, lng;
        if (user?.latitude && user?.longitude) {
          lat = user.latitude;
          lng = user.longitude;
        } else {
          try {
            const pos = await new Promise((resolve, reject) =>
              navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
            );
            lat = pos.coords.latitude;
            lng = pos.coords.longitude;
          } catch {
            lat = 29.6516;
            lng = -82.3248;
          }
        }
        const res = await axios.get(`${API}/golf/courses/nearby`, { params: { lat, lng } });
        const c = res.data?.courses || [];
        setNearbyCourses(c);
        setCourses(c);
      } catch {}
      finally { setLoadingNearby(false); }
    };
    fetchNearby();
  }, [user]);

  useEffect(() => {
    if (!query.trim()) { setCourses(nearbyCourses); return; }
    const q = query.toLowerCase();
    const local = nearbyCourses.filter(c =>
      (c.course_name || c.club_name || '').toLowerCase().includes(q) ||
      (c.location?.city || '').toLowerCase().includes(q)
    );
    setCourses(local);
  }, [query, nearbyCourses]);

  useEffect(() => {
    if (query.length < 3) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/golf/courses/search`, { params: { q: query } });
        const apiCourses = res.data?.courses || [];
        if (apiCourses.length > 0) {
          setCourses(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const newC = apiCourses.filter(c => !existingIds.has(c.id));
            return [...prev, ...newC];
          });
        }
      } catch {}
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (course) => {
    const name = course.course_name || course.club_name;
    setQuery(name);
    setShowDropdown(false);
    onCourseSelect(course);
    onCourseNameChange(name);
  };

  return (
    <div className="space-y-1 relative">
      <Label className="text-xs font-semibold uppercase text-muted-foreground">Course *</Label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        {loadingNearby && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
        <Input
          ref={inputRef}
          placeholder="Search for a course..."
          value={query}
          onChange={e => { setQuery(e.target.value); onCourseNameChange(e.target.value); onCourseSelect(null); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          className="h-11 rounded-lg pl-10"
          data-testid="course-name-input"
        />
      </div>
      {showDropdown && (
        <div ref={dropdownRef} className="absolute left-0 right-0 top-full z-30 mt-1 rounded-xl shadow-xl border border-border max-h-56 overflow-y-auto bg-card" data-testid="course-dropdown">
          {courses.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              {loadingNearby ? 'Loading nearby courses...' : query.length > 0 ? 'No courses found.' : 'Loading...'}
            </div>
          ) : (
            courses.slice(0, 15).map((course, idx) => (
              <button
                key={course.id || idx}
                type="button"
                onClick={() => handleSelect(course)}
                className="w-full px-4 py-3 text-left transition-colors flex items-start gap-3 border-b border-border last:border-b-0 hover:bg-muted/50"
                data-testid={`course-option-${idx}`}
              >
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{course.course_name || course.club_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {[course.location?.address, course.location?.city, course.location?.state].filter(Boolean).join(', ')}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// =================== CREATE: STEP 2 — PLAYERS + STAKES ===================
const SetupPlayers = ({ gameType, onBack, onCreated }) => {
  const { user } = useAuth();
  const gameInfo = GAME_TYPES.find(g => g.value === gameType);
  const [courseName, setCourseName] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTee, setSelectedTee] = useState(null);
  const [players, setPlayers] = useState([{ name: '', handicap: 0, user_id: null }, { name: '', handicap: 0, user_id: null }]);
  const [stake, setStake] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [useTeams, setUseTeams] = useState(!!gameInfo?.requireTeams);
  const [teams, setTeams] = useState([{ name: 'Team A', player_names: [] }, { name: 'Team B', player_names: [] }]);

  const [friends, setFriends] = useState([]);
  const [showFriendPicker, setShowFriendPicker] = useState(null);

  useEffect(() => {
    axios.get(`${API}/following`).then(r => setFriends(r.data)).catch(() => {});
  }, []);

  // Include yourself at the top of the picker list
  const selfOption = user ? {
    id: user.id,
    display_name: user.display_name || user.username,
    username: user.username,
    handicap: user.handicap ?? user.effective_handicap ?? 0,
    profile_photo: user.profile_photo,
    _self: true,
  } : null;
  const pickable = selfOption && !friends.some(f => f.id === selfOption.id) ? [selfOption, ...friends] : friends;

  const tees = selectedCourse?.tees?.male || selectedCourse?.tees?.female || [];

  const addPlayer = () => setPlayers([...players, { name: '', handicap: 0, user_id: null }]);
  const removePlayer = (i) => players.length > 2 && setPlayers(players.filter((_, idx) => idx !== i));
  const updatePlayer = (i, field, val) => {
    const u = [...players];
    u[i] = { ...u[i], [field]: field === 'handicap' ? Number(val) : val };
    setPlayers(u);
  };

  const selectFriend = (i, friend) => {
    const u = [...players];
    u[i] = { name: friend.display_name || friend.username, handicap: friend.handicap || 0, user_id: friend.id };
    setPlayers(u);
    setShowFriendPicker(null);
  };

  const [parPreview, setParPreview] = useState(null); // {pars, source} or null
  const [parChecking, setParChecking] = useState(false);
  const [requestingCourse, setRequestingCourse] = useState(false);
  const [courseRequested, setCourseRequested] = useState(false);
  const [manualParsOpen, setManualParsOpen] = useState(false);
  const [manualPars, setManualPars] = useState(Array(18).fill(4));
  const [ocrLoading, setOcrLoading] = useState(false);
  const ocrFileRef = useRef(null);

  const handleScorecardPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.match(/^image\/(jpeg|png|webp|jpg)$/)) {
      toast.error('Please pick a JPEG, PNG, or WEBP image');
      return;
    }
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/golf/courses/ocr-scorecard`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const data = res.data || {};
      if (Array.isArray(data.pars) && data.pars.length === 18) {
        setManualPars(data.pars);
        setManualParsOpen(true);
        toast.success(`Scorecard read — Par ${data.pars.reduce((a, b) => a + b, 0)} (${data.confidence || 'medium'} confidence). Review and edit if needed.`);
      } else {
        toast.error(data.notes || "Couldn't read 18 pars from this photo. Try a sharper image or enter pars manually.");
        setManualParsOpen(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'OCR failed — try a clearer photo or enter pars manually');
    } finally {
      setOcrLoading(false);
      if (event.target) event.target.value = '';
    }
  };

  const handleCourseSelect = async (course) => {
    setSelectedCourse(course);
    setSelectedTee(null);
    setParPreview(null);
    setCourseRequested(false);
    setManualParsOpen(false);
    setManualPars(Array(18).fill(4));

    // Nearby search returns lightweight courses without tees/pars. If the
    // course has a real Golf Course API id (numeric, not 'gp_*'), fetch the
    // full record so we get per-hole pars before the round is created.
    let fullCourse = course;
    if (course?.id && !String(course.id).startsWith('gp_') && !course?.tees) {
      try {
        const res = await axios.get(`${API}/golf/courses/${course.id}`);
        if (res.data?.course) {
          fullCourse = { ...course, ...res.data.course };
          setSelectedCourse(fullCourse);
        }
      } catch (e) {
        // 422 means the course has no tee data (Google Places gp_ courses) — fall through to resolver
      }
    }

    const maleTees = fullCourse?.tees?.male || [];
    if (maleTees.length > 0) {
      setSelectedTee(maleTees[0]);
      return;  // We have official tee data — no need to call the resolver
    }

    // No tee data yet — call the resolver immediately so the user gets
    // instant feedback ("✓ Scorecard found" or "✗ Course not in our database").
    setParChecking(true);
    try {
      const loc = fullCourse?.location || {};
      const res = await axios.get(`${API}/golf/courses/resolve-pars`, {
        params: {
          name: fullCourse?.course_name || fullCourse?.club_name || course?.club_name || '',
          course_id: fullCourse?.id ? String(fullCourse.id) : '',
          lat: loc.latitude ?? null,
          lng: loc.longitude ?? null,
        },
      });
      setParPreview(res.data);
    } catch (e) {
      setParPreview({ pars: [], source: 'unknown' });
    } finally {
      setParChecking(false);
    }
  };

  const togglePlayerTeam = (playerName, teamIdx) => {
    const newTeams = teams.map((t, i) => ({
      ...t,
      player_names: i === teamIdx
        ? (t.player_names.includes(playerName) ? t.player_names.filter(n => n !== playerName) : [...t.player_names, playerName])
        : t.player_names.filter(n => n !== playerName),
    }));
    setTeams(newTeams);
  };

  const handleCreate = async () => {
    const valid = players.filter(p => p.name.trim());
    if (valid.length < 2) { toast.error('Need at least 2 players'); return; }
    if (!courseName.trim()) { toast.error('Enter a course name'); return; }
    if (useTeams) {
      const assigned = teams.reduce((acc, t) => acc + t.player_names.length, 0);
      if (assigned < valid.length) { toast.error('Assign all players to teams'); return; }
    }
    setSubmitting(true);
    try {
      // Priority: official tee data > manual pars typed by user > auto-resolver on backend
      const teePars = selectedTee?.holes?.map(h => h.par) || [];
      const usingManual = manualParsOpen && teePars.length === 0;
      const pars = teePars.length > 0 ? teePars : (usingManual ? manualPars : []);
      const loc = selectedCourse?.location || {};

      // If user typed pars manually for an unknown course, save them as a
      // pending-review scorecard so the next user at the same course benefits.
      if (usingManual) {
        try {
          await axios.post(`${API}/golf/courses/submit-scorecard`, {
            course_name: courseName,
            pars: manualPars,
            lat: loc.latitude ?? null,
            lng: loc.longitude ?? null,
          });
        } catch (e) {
          // Non-blocking — round can still start even if save fails
        }
      }

      const res = await axios.post(`${API}/betting/rounds`, {
        course_name: courseName,
        course_id: selectedCourse?.id ? String(selectedCourse.id) : null,
        tee_name: selectedTee?.tee_name || null,
        players: valid,
        teams: useTeams ? teams.filter(t => t.player_names.length > 0) : null,
        games: [{ type: gameType, stake: Number(stake), settings: {} }],
        pars,
        course_lat: loc.latitude ?? null,
        course_lng: loc.longitude ?? null,
      });
      toast.success(usingManual ? 'Game started — thanks for the scorecard!' : 'Game started!');
      onCreated(res.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const usedIds = new Set(players.filter(p => p.user_id).map(p => p.user_id));
  const validPlayers = players.filter(p => p.name.trim());
  const manualParTotal = manualPars.reduce((a, b) => a + b, 0);
  const manualParsValid = manualPars.every(p => p >= 3 && p <= 6);
  // Block Start Game ONLY when scorecard is missing AND user hasn't filled in manual pars
  const courseMissingScorecard =
    !!selectedCourse && tees.length === 0 && parPreview && parPreview.source === 'unknown' && !parChecking && !(manualParsOpen && manualParsValid);

  const requestThisCourse = async () => {
    if (!selectedCourse && !courseName) return;
    setRequestingCourse(true);
    try {
      const loc = selectedCourse?.location || {};
      const res = await axios.post(`${API}/courses/request`, {
        course_name: selectedCourse?.course_name || selectedCourse?.club_name || courseName,
        lat: loc.latitude ?? null,
        lng: loc.longitude ?? null,
        address: loc.address || null,
      });
      if (res.data.already_requested) {
        toast.success("You've already requested this course — we'll email you when it's added");
      } else {
        toast.success("Course requested — we'll email you within hours when it's ready");
      }
      setCourseRequested(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    } finally {
      setRequestingCourse(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full"><ArrowLeft className="w-5 h-5" /></Button>
        <div>
          <h2 className="font-serif text-lg font-bold text-foreground">{gameInfo?.label} Setup</h2>
          <p className="text-xs text-muted-foreground">{gameInfo?.desc}</p>
        </div>
      </div>

      <div className="space-y-5">
        <CourseSearchInput
          courseName={courseName}
          onCourseSelect={handleCourseSelect}
          onCourseNameChange={setCourseName}
        />

        {tees.length > 0 && (
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase text-muted-foreground">Tee Box</Label>
            <div className="flex gap-2 flex-wrap">
              {tees.map(tee => (
                <button
                  key={tee.tee_name}
                  onClick={() => setSelectedTee(tee)}
                  className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${selectedTee?.tee_name === tee.tee_name ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                  data-testid={`tee-${tee.tee_name}`}
                >
                  {tee.tee_name} ({tee.total_yards}y)
                </button>
              ))}
            </div>
            {selectedTee && (
              <p className="text-xs text-muted-foreground">
                Par {selectedTee.par_total} | Rating {selectedTee.course_rating} / Slope {selectedTee.slope_rating}
              </p>
            )}
          </div>
        )}

        {selectedCourse && tees.length === 0 && (
          <div data-testid="par-preview-banner">
            {parChecking && (
              <div className="px-3 py-2 rounded-lg bg-muted/50 text-xs text-muted-foreground flex items-center gap-2" data-testid="par-preview-checking">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Looking up scorecard for this course…
              </div>
            )}
            {!parChecking && parPreview?.pars?.length === 18 && (
              <div className="px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20 text-xs text-green-700 dark:text-green-400" data-testid="par-preview-found">
                Scorecard ready — par {parPreview.pars.reduce((a,b)=>a+b,0)} ({parPreview.pars.filter(p=>p===3).length} par 3s, {parPreview.pars.filter(p=>p===4).length} par 4s, {parPreview.pars.filter(p=>p===5).length} par 5s)
              </div>
            )}
            {!parChecking && parPreview?.source === 'unknown' && (
              <div className="px-3 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs space-y-2.5" data-testid="par-preview-missing">
                <p className="text-amber-700 dark:text-amber-400 font-medium">
                  We don&apos;t have a scorecard for this course yet. Snap a photo of the scorecard or enter pars below — we&apos;ll save it so other golfers don&apos;t have to.
                </p>

                {!manualParsOpen ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => ocrFileRef.current?.click()}
                      disabled={ocrLoading}
                      size="sm"
                      className="rounded-full text-xs h-8 gap-1.5"
                      data-testid="scan-scorecard-btn"
                    >
                      {ocrLoading ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading photo…</>
                      ) : (
                        <><Camera className="w-3.5 h-3.5" /> Scan Scorecard Photo</>
                      )}
                    </Button>
                    <input
                      ref={ocrFileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleScorecardPhoto}
                      className="hidden"
                      data-testid="scan-scorecard-input"
                    />
                    <Button
                      onClick={() => setManualParsOpen(true)}
                      size="sm"
                      variant="outline"
                      className="rounded-full text-xs h-8 gap-1.5"
                      data-testid="enter-pars-manually-btn"
                    >
                      <Edit3 className="w-3.5 h-3.5" /> Enter Pars Manually
                    </Button>
                    {courseRequested ? (
                      <span className="text-foreground text-xs flex items-center gap-1.5" data-testid="course-requested-confirm">
                        <Check className="w-3.5 h-3.5" /> Course requested
                      </span>
                    ) : (
                      <Button
                        onClick={requestThisCourse}
                        disabled={requestingCourse}
                        size="sm"
                        variant="ghost"
                        className="rounded-full text-xs h-8 gap-1.5"
                        data-testid="request-course-btn"
                      >
                        {requestingCourse ? 'Sending…' : <><MailIcon className="w-3.5 h-3.5" /> Request curated version</>}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="manual-pars-grid">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Front 9</span>
                      <span className="text-[10px] text-muted-foreground">Tap a box, type 3–6</span>
                    </div>
                    <div className="grid grid-cols-9 gap-1">
                      {manualPars.slice(0, 9).map((p, i) => (
                        <div key={i} className="flex flex-col items-center">
                          <span className="text-[9px] text-muted-foreground leading-none mb-0.5">{i + 1}</span>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="3"
                            max="6"
                            value={p}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^3-6]/g, '');
                              const digit = raw.length ? Number(raw[raw.length - 1]) : 4;
                              const next = [...manualPars];
                              next[i] = digit >= 3 && digit <= 6 ? digit : 4;
                              setManualPars(next);
                            }}
                            className="w-full text-center bg-card border border-border focus:border-primary focus:outline-none rounded-md py-1.5 text-base font-bold text-foreground"
                            data-testid={`manual-par-hole-${i + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Back 9</span>
                    </div>
                    <div className="grid grid-cols-9 gap-1">
                      {manualPars.slice(9, 18).map((p, i) => (
                        <div key={i + 9} className="flex flex-col items-center">
                          <span className="text-[9px] text-muted-foreground leading-none mb-0.5">{i + 10}</span>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="3"
                            max="6"
                            value={p}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^3-6]/g, '');
                              const digit = raw.length ? Number(raw[raw.length - 1]) : 4;
                              const next = [...manualPars];
                              next[i + 9] = digit >= 3 && digit <= 6 ? digit : 4;
                              setManualPars(next);
                            }}
                            className="w-full text-center bg-card border border-border focus:border-primary focus:outline-none rounded-md py-1.5 text-base font-bold text-foreground"
                            data-testid={`manual-par-hole-${i + 10}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-1.5 border-t border-amber-500/20">
                      <span className="text-xs font-semibold text-foreground" data-testid="manual-par-total">
                        Total Par: {manualParTotal}
                      </span>
                      <button
                        type="button"
                        onClick={() => { setManualParsOpen(false); setManualPars(Array(18).fill(4)); }}
                        className="text-[11px] text-muted-foreground hover:text-foreground underline"
                        data-testid="manual-pars-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase text-muted-foreground">Players</Label>

          {players.map((p, i) => {
            const q = (p.name || '').toLowerCase().trim();
            // Friends not yet picked elsewhere — but show the current row's linked friend too
            const pool = pickable.filter(f => !usedIds.has(f.id) || f.id === p.user_id);
            const matches = q
              ? pool.filter(f => (f.display_name || f.username || '').toLowerCase().includes(q))
              : pool;
            const showDropdown = showFriendPicker === i && matches.length > 0;
            return (
              <div key={i} className="space-y-1">
                <div className="flex gap-2 items-center relative">
                  <div className="relative flex-1">
                    <Input
                      placeholder={pickable.length > 0 ? `Player ${i + 1} — type a name or pick a friend` : `Player ${i + 1}`}
                      value={p.name}
                      onFocus={() => pickable.length > 0 && setShowFriendPicker(i)}
                      onBlur={() => setTimeout(() => setShowFriendPicker(curr => curr === i ? null : curr), 150)}
                      onChange={e => {
                        const val = e.target.value;
                        const u = [...players];
                        // If user edits a row that was linked to a friend, unlink it
                        u[i] = { ...u[i], name: val, user_id: u[i].user_id && val !== u[i].name ? null : u[i].user_id };
                        setPlayers(u);
                        if (pickable.length > 0) setShowFriendPicker(i);
                      }}
                      className={`h-10 rounded-lg pr-9 ${p.user_id ? 'border-primary/40' : ''}`}
                      data-testid={`player-name-${i}`}
                    />
                    {p.user_id ? (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-primary pointer-events-none" title="Linked to a friend">
                        <Check className="w-4 h-4" />
                      </span>
                    ) : pickable.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setShowFriendPicker(showFriendPicker === i ? null : i)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                        data-testid={`friend-picker-btn-${i}`}
                        title="Search friends"
                      >
                        <Search className="w-4 h-4" />
                      </button>
                    ) : null}
                  </div>
                  <Input type="number" placeholder="Hcp" value={p.handicap || ''} onChange={e => updatePlayer(i, 'handicap', e.target.value)} className="h-10 rounded-lg w-16 text-center" data-testid={`player-hdcp-${i}`} />
                  {players.length > 2 && <button onClick={() => removePlayer(i)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-player-${i}`}><X className="w-4 h-4" /></button>}
                </div>
                {showDropdown && (
                  <div
                    className="rounded-xl border border-border bg-card shadow-lg max-h-48 overflow-y-auto"
                    style={{ marginRight: players.length > 2 ? 88 : 72 }}
                    data-testid={`friend-dropdown-${i}`}
                  >
                    {matches.map(f => (
                      <button
                        key={f.id}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); selectFriend(i, f); }}
                        className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-muted/50 transition-colors"
                        data-testid={`friend-option-${i}-${f.id}`}
                      >
                        {f.profile_photo ? (
                          <img src={f.profile_photo} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                            {(f.display_name || f.username || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {f.display_name || f.username}
                            {f._self && <span className="ml-1.5 text-[10px] font-bold text-primary uppercase">(You)</span>}
                          </p>
                          {f.handicap != null && <p className="text-[10px] text-muted-foreground">Hcp {f.handicap}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <Button variant="outline" size="sm" onClick={addPlayer} className="rounded-full text-xs gap-1" data-testid="add-player-btn">
            <Plus className="w-3 h-3" /> Add Player
          </Button>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase text-muted-foreground">Stake per unit ($)</Label>
          <div className="flex gap-2">
            {[1, 2, 5, 10, 20].map(v => (
              <button key={v} onClick={() => setStake(v)} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${stake === v ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`} data-testid={`stake-${v}`}>
                ${v}
              </button>
            ))}
            <Input type="number" value={stake} onChange={e => setStake(e.target.value)} className="w-20 h-10 rounded-lg text-center" />
          </div>
        </div>

        {(gameInfo?.teamable || gameInfo?.requireTeams) && (
          <div className="space-y-3">
            {!gameInfo?.requireTeams && (
              <div className="flex items-center gap-3">
                <Label className="text-xs font-semibold uppercase text-muted-foreground">Teams</Label>
                <button
                  onClick={() => setUseTeams(!useTeams)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${useTeams ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                  data-testid="toggle-teams-btn"
                >
                  {useTeams ? 'Teams ON' : 'Individual'}
                </button>
              </div>
            )}
            {useTeams && validPlayers.length >= 2 && (
              <div className="space-y-2">
                {teams.map((team, ti) => (
                  <div key={ti} className="rounded-lg border border-border p-3 bg-muted/30">
                    <Input
                      value={team.name}
                      onChange={e => setTeams(teams.map((t, i) => i === ti ? { ...t, name: e.target.value } : t))}
                      className="h-8 rounded-md text-sm font-bold mb-2 bg-card"
                      data-testid={`team-name-${ti}`}
                    />
                    <div className="flex flex-wrap gap-1.5">
                      {validPlayers.map(p => {
                        const inThisTeam = team.player_names.includes(p.name);
                        const inOther = teams.some((t, i) => i !== ti && t.player_names.includes(p.name));
                        return (
                          <button
                            key={p.name}
                            onClick={() => togglePlayerTeam(p.name, ti)}
                            disabled={inOther}
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${inThisTeam ? 'bg-primary text-primary-foreground' : inOther ? 'bg-muted/50 text-muted-foreground/50' : 'border border-border bg-card text-foreground hover:bg-primary/10'}`}
                            data-testid={`team-${ti}-player-${p.name}`}
                          >
                            {p.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <Button onClick={handleCreate} disabled={submitting || courseMissingScorecard} className="w-full h-12 rounded-lg gap-2 text-base font-semibold" data-testid="start-game-btn">
          <Trophy className="w-5 h-5" />
          {submitting
            ? 'Starting...'
            : courseMissingScorecard
              ? 'Enter Pars to Continue'
              : (manualParsOpen && tees.length === 0)
                ? `Start Game (Par ${manualParTotal})`
                : 'Start Game'}
        </Button>
      </div>
    </div>
  );
};

// =================== MAIN PAGE ===================
const Betting = () => {
  const [view, setView] = useState('list');
  const [selectedGame, setSelectedGame] = useState(null);
  const [activeRoundId, setActiveRoundId] = useState(null);
  const [tab, setTab] = useState('games');

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="betting-page">
      <div className="px-4 py-3 sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur-xl">
        <h1 className="font-serif text-2xl font-bold text-foreground flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-primary" /> Side Games
        </h1>
        {view === 'list' && (
          <div className="flex gap-1 mt-2">
            <button
              onClick={() => setTab('games')}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${tab === 'games' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
              data-testid="tab-games"
            >
              My Games
            </button>
            <button
              onClick={() => setTab('leaderboard')}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${tab === 'leaderboard' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
              data-testid="tab-leaderboard"
            >
              Leaderboard
            </button>
          </div>
        )}
      </div>

      <div className="max-w-lg mx-auto px-4 pt-4">
        {view === 'list' && tab === 'games' && (
          <RoundList
            onCreateNew={() => setView('select-game')}
            onSelectRound={(id) => { setActiveRoundId(id); setView('scoring'); }}
          />
        )}
        {view === 'list' && tab === 'leaderboard' && (
          <Leaderboard />
        )}
        {view === 'select-game' && (
          <GameSelector
            onSelect={(g) => { setSelectedGame(g); setView('setup'); }}
            onBack={() => setView('list')}
          />
        )}
        {view === 'setup' && selectedGame && (
          <SetupPlayers
            gameType={selectedGame}
            onBack={() => setView('select-game')}
            onCreated={(id) => { setActiveRoundId(id); setView('scoring'); }}
          />
        )}
        {view === 'scoring' && activeRoundId && (
          <LiveScoring
            roundId={activeRoundId}
            onBack={() => { setView('list'); setActiveRoundId(null); }}
          />
        )}
      </div>

      <Navigation />
    </div>
  );
};

export default Betting;
